import type { StudioCommandType, StudioQueueItem, StudioSnapshot, StudioTrack } from './types';

export interface StudioTiming {
  elapsedMs: number;
  remainingMs: number;
  introMs: number | null;
  vocalMs: number | null;
  outroMs: number | null;
  progress: number;
}

export function projectTiming(snapshot: StudioSnapshot, receivedAtMs: number, nowMs: number): StudioTiming {
  const track = snapshot.current;
  if (track === null) return { elapsedMs: 0, remainingMs: 0, introMs: null, vocalMs: null, outroMs: null, progress: 0 };
  const driftMs = Math.max(0, nowMs - receivedAtMs);
  const elapsedMs = Math.min(track.durationMs, Math.max(0, snapshot.runtime.elapsedSeconds * 1000 + driftMs));
  const remainingMs = Math.max(0, snapshot.runtime.remainingSeconds * 1000 - driftMs);

  return {
    elapsedMs,
    remainingMs,
    introMs: countdown(track.cuePoints.introEndMs, elapsedMs),
    vocalMs: countdown(track.cuePoints.vocalStartMs, elapsedMs),
    outroMs: countdown(track.cuePoints.outroStartMs, elapsedMs),
    progress: track.durationMs === 0 ? 0 : Math.min(1, elapsedMs / track.durationMs),
  };
}

export function canRunCommand(snapshot: StudioSnapshot, type: StudioCommandType): boolean {
  if (!snapshot.capabilities.canControl || !['STARTING', 'ON_AIR'].includes(snapshot.runtime.engineState)) return false;
  if (type === 'TAKE_CONTROL') return snapshot.runtime.engineState === 'ON_AIR' && snapshot.runtime.effectiveMode === 'AUTO';
  if (type === 'RETURN_AUTO') return snapshot.runtime.engineState === 'ON_AIR' && snapshot.runtime.effectiveMode === 'ASSIST';
  return snapshot.runtime.effectiveMode === 'ASSIST';
}

export function formatDuration(milliseconds: number | null): string {
  if (milliseconds === null) return '—';
  const bounded = Math.max(0, Math.round(milliseconds));
  const minutes = Math.floor(bounded / 60_000);
  const seconds = Math.floor((bounded % 60_000) / 1000);
  const tenths = Math.floor((bounded % 1000) / 100);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${tenths}`;
}

export function isEndingSoon(remainingMs: number | null): boolean {
  return remainingMs !== null && remainingMs > 0 && remainingMs <= 10_000;
}

export function upcomingQueue(queue: StudioQueueItem[]): StudioQueueItem[] {
  return queue.filter((item) => !item.isCurrent && item.state !== 'PLAYING');
}

export function waveformBars(track: StudioTrack | null, count = 72): number[] {
  if (track === null || track.waveformPeaks.length === 0) {
    return Array.from({ length: count }, (_, index) => 0.12 + ((index * 17) % 9) / 50);
  }
  const stride = Math.max(1, Math.ceil(track.waveformPeaks.length / count));
  return Array.from({ length: count }, (_, index) => {
    const chunk = track.waveformPeaks.slice(index * stride, (index + 1) * stride);
    return Math.max(0.06, ...chunk);
  });
}

export function shortcutLabel(code: string | null): string {
  if (code === null) return '';
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Key')) return code.slice(3);
  return code;
}

function countdown(markerMs: number | null, elapsedMs: number): number | null {
  return markerMs === null || markerMs <= elapsedMs ? null : markerMs - elapsedMs;
}
